import React, { ForwardedRef, forwardRef, useMemo } from 'react'
import { Collapse, IconButton, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import FocusFadeGroup from 'components/FocusFadeGroup'
import TextArea from 'components/TextArea'
import { Create } from '@mui/icons-material'
import { useFragment, graphql } from 'react-relay'
import { TextIntroductionField_User$key } from '__generated__/TextIntroductionField_User.graphql'
import { useForm, useMutationBasedFormSubmitHandlers } from 'hooks/form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import useStyles from './styles'

const PLACEHOLDER = 'Write a few lines about yourself and your professional background...'

const formSchema = z.object({
  textIntroduction: z.string().min(1, { message: 'Required' }),
})

type FormFields = z.infer<typeof formSchema>

export interface ITextIntroductionFieldProps {
  editable: boolean
  user: TextIntroductionField_User$key
}

function TextIntroductionField(props: ITextIntroductionFieldProps, ref: ForwardedRef<HTMLTextAreaElement>) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, user: userFragmentRef } = props
  const [isEditMode, setEditMode] = React.useState(false)
  const textAreaWrapperRef = React.useRef<HTMLDivElement>(null)

  const user = useFragment(graphql`
    fragment TextIntroductionField_User on User {
      profile {
        textIntroduction
      }
    }
  `, userFragmentRef)

  const form = useForm<FormFields>({
    enableReinitialize: true,
    defaultValues: useMemo(() => ({
      textIntroduction: user.profile?.textIntroduction || '',
    }), [user]),
    resolver: zodResolver(formSchema),
    submitHandlers: useMutationBasedFormSubmitHandlers(graphql`
      mutation TextIntroductionField_UpdateUserMutation($input: UpdateUserInput!) {
        updateUser(input: $input) {
          user {
            profile {
              textIntroduction
            }
          }
        }
      }
    `, values => ({
      input: {
        profile: {
          textIntroduction: values.textIntroduction,
        },
      },
    }), {
      successMessage: 'Introduction updated',
      verifySuccess: (result) => {
        return Boolean(result?.updateUser?.user?.profile?.textIntroduction)
      },
    }),
  })

  const textIntroduction = form.useFormField('textIntroduction', 'selection')

  const handleEditClick = () => {
    if (!editable) return

    setEditMode(true)

    window.setTimeout(() => {
      textAreaWrapperRef.current?.querySelector('textarea')?.focus()
    }, 100)
  }

  const handleBlur = (e: React.FocusEvent<unknown>) => {
    if (textIntroduction.input.onBlur) {
      textIntroduction.input.onBlur(e)
    }

    setEditMode(false)
    form.submit()
  }

  if (!textIntroduction.input.value && !editable) {
    return null
  }

  const validationError = textIntroduction.input.value || !editable ? null : 'Required'
  const error = textIntroduction.meta.error || validationError

  return (
    <FocusFadeGroup focused={isEditMode}>
      <Collapse in={!isEditMode}>
        <div className={classes.container} onClick={handleEditClick} role="button">
          <div className={classes.text} style={editable ? { paddingRight: theme.spacing(4), paddingLeft: theme.spacing(4) } : undefined}>
            <Typography variant="body1">
              {textIntroduction.input.value || PLACEHOLDER}
            </Typography>

            {error && (
              <Typography variant="body1" className={classes.error} data-cy="profile-introduction-error">
                A few lines about yourself are {error?.toLowerCase()}
              </Typography>
            )}

            {editable && (
              <div className={classes.edit}>
                <Tooltip title="Edit text introduction">
                  <IconButton data-cy="profile-introduction-edit" size="large">
                    <Create />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </Collapse>

      <Collapse in={isEditMode}>
        <div ref={textAreaWrapperRef}>
          <TextArea
            className={classes.textarea}
            placeholder={PLACEHOLDER}
            label="Introduction"
            input={{
              ...textIntroduction.input,
              onBlur: handleBlur,
              name: 'textIntroduction',
            }}
            maxLength={1500}
            ref={ref}
          />
        </div>
      </Collapse>
    </FocusFadeGroup>
  )
}

export default React.memo(forwardRef(TextIntroductionField))
